/* eslint-disable max-lines */
import {
    useContext, useEffect, useState
} from 'react';
import {
    ChevronRight, HelpCircle, X, Mail, CheckCircle2
} from 'lucide-react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Popover, PopoverContent, PopoverTrigger
} from 'shared/components/ui/popover';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    AppLoader
} from 'shared/components/LoaderSpinner';
import {
    Context
} from 'shared/context/GlobalState';
import {
    authService, organizationService
} from 'shared/api';
import {
    RadioGroup, RadioGroupItem
} from 'shared/components/ui/radio-group';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    Alert, AlertDescription
} from 'shared/components/ui/alert';
import {
    redisService
} from 'shared/api/services/BEX/redisService';
import {
    checkNameLengthWithSuffix
} from 'shared/utils/stringUtils';

const TOTAL_STEPS = 4;

const INITIAL_FORM_DATA = {
    orgName: '',
    subOrgName: '',
    members: [],
    redisConfig: {
        useCommon: true,
        host: '',
        port: '6379',
        password: '',
    },
};

const STEP_META = [
    {
        title: 'Name your organization',
        description: 'Choose a name for your top-level organization.',
    },
    {
        title: 'Configure sub-organization',
        description: 'Set up the default sub-organization for your teams and projects.',
    },
    {
        title: 'Add team members',
        description: 'Invite colleagues to join. This step is optional.',
    },
    {
        title: 'Configure Redis',
        description: 'Choose the Redis instance for your organization.',
    },
];

function HelpHint({
    text
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="h-6 w-6">
                    <HelpCircle className="h-3.5 w-3.5" />
                    <span className="sr-only">Show help</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="max-w-sm text-sm" align="start">
                <p>{text}</p>
            </PopoverContent>
        </Popover>
    );
}

export function CreateOrganizationFullscreen({
    open, onClose
}) {
    const [step, setStep] = useState(1);
    const [isCreatingOrg, setIsCreatingOrg] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [isInvalidRedis, setIsInvalidRedis] = useState(null);
    const [redisConnectionValidated, setRedisConnectionValidated] = useState(false);
    const [isOrganizationNameValid, setIsOrganizationNameValid] = useState(null);
    const [isSubOrganizationNameValid, setIsSubOrganizationNameValid] = useState(null);
    const [checkingOrgName, setCheckingOrgName] = useState(false);
    const [checkingRedisConnection, setCheckingRedisConnection] = useState(false);
    const [isAdvancing, setIsAdvancing] = useState(false);
    const [fetchingUser, setFetchingUser] = useState(false);
    const [emailSending, setEmailSending] = useState(null);
    const [isEmailValid, setIsEmailValid] = useState(null);
    const [newMemberRole, setNewMemberRole] = useState('admin');
    const [orgId, setOrgId] = useState(null);

    const {
        user, addNewNotifcation
    } = useContext(Context);

    const resetWizard = () => {
        setStep(1);
        setIsCreatingOrg(false);
        setFormData(INITIAL_FORM_DATA);
        setNewMemberEmail('');
        setIsInvalidRedis(null);
        setRedisConnectionValidated(false);
        setIsOrganizationNameValid(null);
        setIsSubOrganizationNameValid(null);
        setCheckingOrgName(false);
        setCheckingRedisConnection(false);
        setIsAdvancing(false);
        setFetchingUser(false);
        setEmailSending(null);
        setIsEmailValid(null);
        setNewMemberRole('admin');
        setOrgId(null);
    };

    const handleOpenChange = (nextOpen) => {
        if (isCreatingOrg) return;
        if (!nextOpen) {
            resetWizard();
            onClose();
        }
    };

    useEffect(() => {
        let timeoutId;

        const checkStatus = async () => {
            if (orgId) {
                const res = await checkDeploymentStatus();
                if (res) {
                    handleOrgCreationComplete();
                } else {
                    timeoutId = setTimeout(checkStatus, 5000);
                }
            }
        };

        checkStatus();

        return () => clearTimeout(timeoutId);
    }, [orgId]);

    const updateFormData = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleNext = async (createOrganization) => {
        if (isAdvancing) return;

        if (step === 1) {
            if (!formData.orgName.trim()) return;
            setIsAdvancing(true);
            const isValid = await validateOrganizationName();
            setIsAdvancing(false);
            if (!isValid) return;
        }

        if (step === 2 && !createOrganization) {
            if (!formData.subOrgName.trim()) return;
            if (!validateSubOrganizationName()) return;
        }

        if (createOrganization) {
            if (!formData.subOrgName.trim()) return;
            if (!validateSubOrganizationName()) return;

            if (!formData.redisConfig.useCommon && !redisConnectionValidated) {
                addNewNotifcation('Please test your Redis connection before creating the organization.', 'warning');
                return;
            }
        }

        if (!createOrganization) {
            setStep(step + 1);
            return;
        }

        setIsCreatingOrg(true);
        const orgObj = {
            name: formData.orgName.trim(),
            subOrgDefaultName: formData.subOrgName.trim(),
            created: new Date(),
            members: formData.members.map((member) => ({
                id: member.id ? member.id : member.email,
                role: member.role,
                status: member.status
            })),
            createdBy: user._id,
            superAdmin: user._id,
        };
        if (!formData.redisConfig.useCommon) {
            const {
                host, port, password
            } = formData.redisConfig;
            orgObj['redisKeys'] = {
                host,
                port,
                password
            };
        }

        organizationService.createOrganization(orgObj)
            .then((res) => {
                setOrgId(res.data._id);
            }).catch((error) => {
                console.error('Error creating organization:', error);
                setIsCreatingOrg(false);
            });
    };

    const checkDeploymentStatus = async () => {
        try {
            const response = await organizationService.checkOrganizationCluster(orgId);
            if (response.status === 202) {
                return false;
            }
            return true;
        } catch (error) {
            console.error('Error checking deployment status:', error);
            return false;
        }
    };

    const handleBack = () => {
        if (step > 1) {
            setStep(step - 1);
        } else {
            handleOpenChange(false);
        }
    };

    const handleOrgCreationComplete = () => {
        window.location = `/organizations/${orgId}/sub`;
    };

    const addMember = async () => {
        if (!newMemberEmail) return;

        setIsEmailValid(null);
        setEmailSending(null);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newMemberEmail.trim())) {
            setIsEmailValid('Please enter valid email');
            return;
        } else if (formData.members.some((member) => member.email === newMemberEmail.trim())) {
            setIsEmailValid('Email already added');
            return;
        }

        setFetchingUser(true);
        const userObj = {
            email: newMemberEmail.trim(),
            role: newMemberRole
        };

        try {
            const userResponse = await authService.getUserByEmail(newMemberEmail.trim());
            if (userResponse.data._id === user._id) {
                setIsEmailValid('You cannot add yourself as a member');
                return;
            }
            userObj['id'] = userResponse.data._id;
            userObj['status'] = 'registered';
        } catch (error) {
            console.error('Error fetching user by email:', error);
            userObj['status'] = 'pending';
            setEmailSending(newMemberEmail.trim());
        } finally {
            setFetchingUser(false);
        }
        setFormData((prev) => ({
            ...prev,
            members: [...prev.members, userObj],
        }));

        setNewMemberEmail('');
        setNewMemberRole('admin');
    };

    const removeMember = (email) => {
        setFormData((prev) => ({
            ...prev,
            members: prev.members.filter((member) => member.email !== email),
        }));
    };

    const clearRedisValidation = () => {
        setIsInvalidRedis(null);
        setRedisConnectionValidated(false);
    };

    const updateRedisConfig = (field, value) => {
        clearRedisValidation();
        setFormData((prev) => ({
            ...prev,
            redisConfig: {
                ...prev.redisConfig,
                [field]: value,
            },
        }));
    };

    const validateRedisConfig = async () => {
        clearRedisValidation();
        const {
            host, port, password
        } = formData.redisConfig;
        if (!host?.trim() || !port?.trim() || !password) {
            setIsInvalidRedis('Please fill in host, port, and password before testing.');
            return false;
        }
        setCheckingRedisConnection(true);

        try {
            await redisService.checkRedisClient({
                host: host.trim(),
                port: port.trim(),
                password,
            });
            setRedisConnectionValidated(true);
            return true;
        } catch (error) {
            console.error('Error checking Redis connection:', error);
            setIsInvalidRedis('Could not connect to Redis. Please check your host, port, and password.');
            return false;
        } finally {
            setCheckingRedisConnection(false);
        }
    };

    const handleTestRedisConnection = () => {
        validateRedisConfig();
    };

    const validateOrganizationName = async () => {
        setIsOrganizationNameValid(null);
        const orgNameRegex = /^[a-zA-Z0-9_ -]+$/;

        if (formData.orgName.trim().length < 4) {
            setIsOrganizationNameValid('Organization name must be more than 4 character');
            return false;
        }
        if (!orgNameRegex.test(formData.orgName.trim())) {
            setIsOrganizationNameValid('Organization name should not contain special character');
            return false;
        }
        if (checkNameLengthWithSuffix(formData.orgName.trim().replaceAll(' ', '-'))) {
            setIsOrganizationNameValid('Organization name is too long. Please shorten it to fit within 30 characters or less.');
            return false;
        }

        setCheckingOrgName(true);
        try {
            await organizationService.checkOrganizationName(formData.orgName.trim());
            return true;
        } catch (error) {
            console.error('Error checking organization name:', error);
            setIsOrganizationNameValid('organization name already exists');
            return false;
        } finally {
            setCheckingOrgName(false);
        }
    };

    const validateSubOrganizationName = () => {
        setIsSubOrganizationNameValid(null);
        const orgNameRegex = /^[a-zA-Z0-9_ -]+$/;

        if (formData.subOrgName.trim().length < 4) {
            setIsSubOrganizationNameValid('Sub-Organization name must be more than 4 character');
            return false;
        }
        if (!orgNameRegex.test(formData.subOrgName.trim())) {
            setIsSubOrganizationNameValid('Sub-Organization name should not contain special character');
            return false;
        }
        if (checkNameLengthWithSuffix(formData.subOrgName.trim().replaceAll(' ', '-'))) {
            setIsSubOrganizationNameValid('Sub-Organization name is too long. Please shorten it to fit within 30 characters or less.');
            return false;
        }
        if (formData.subOrgName.trim().toLocaleLowerCase() === formData.orgName.trim().toLocaleLowerCase()
            || formData.subOrgName.trim().toLocaleLowerCase() === `${formData.orgName.trim().toLocaleLowerCase()}-main`
            || formData.subOrgName.trim().toLocaleLowerCase() === `${formData.orgName.trim().toLocaleLowerCase()} main`) {
            setIsSubOrganizationNameValid('Preserved name for main organization');
            return false;
        }

        return true;
    };

    const handleOnBlur = async (field) => {
        switch (field) {
            case 'organizationName':
                await validateOrganizationName();
                break;
            case 'subOrganizationName':
                validateSubOrganizationName();
                break;
            default:
                break;
        }
    };

    const isNextDisabled = isAdvancing
        || (step === 1 && isOrganizationNameValid)
        || (step === 2 && isSubOrganizationNameValid);

    const currentStep = STEP_META[step - 1];

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="sm:max-w-[560px] max-h-[90vh] flex flex-col p-0 gap-0"
                onPointerDownOutside={(event) => {
                    if (isCreatingOrg) event.preventDefault();
                }}
                onEscapeKeyDown={(event) => {
                    if (isCreatingOrg) event.preventDefault();
                }}
            >
                {isCreatingOrg ? (
                    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                        <AppLoader size={48} />
                        <h2 className="text-lg font-semibold mt-6">Creating your organization...</h2>
                        <p className="text-sm text-muted-foreground mt-2">
                            Setting up <span className="font-medium">{formData.orgName}</span> and{' '}
                            <span className="font-medium">{formData.subOrgName}</span>.
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">This may take a few minutes.</p>
                    </div>
                ) : (
                    <>
                        <DialogHeader className="px-6 pt-6 pb-4 border-b">
                            <div className="flex items-center justify-between gap-4 pr-6">
                                <DialogTitle>Create Organization</DialogTitle>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                    Step {step} of {TOTAL_STEPS}
                                </span>
                            </div>
                            <DialogDescription>{currentStep.description}</DialogDescription>
                            <div className="flex gap-1 pt-2">
                                {STEP_META.map((_, index) => (
                                    <div
                                        key={index}
                                        className={`h-1 flex-1 rounded-full ${index < step ? 'bg-primary' : 'bg-muted'}`}
                                    />
                                ))}
                            </div>
                        </DialogHeader>

                        <ScrollArea className="max-h-[55vh] px-6 py-4">
                            <div className="space-y-4">
                                <h2 className="text-lg font-semibold">{currentStep.title}</h2>

                                {step === 1 && (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Label htmlFor="orgName">Organization Name</Label>
                                            <HelpHint text="An organization is the top-level entity that groups all your projects, teams, and resources." />
                                        </div>
                                        <Input
                                            disabled={checkingOrgName}
                                            onBlur={() => handleOnBlur('organizationName')}
                                            id="orgName"
                                            value={formData.orgName}
                                            onChange={(e) => {
                                                setIsOrganizationNameValid(null);
                                                updateFormData('orgName', e.target.value);
                                                updateFormData('subOrgName', `${e.target.value}-sub`);
                                            }}
                                            placeholder="Acme Inc."
                                        />
                                        {isOrganizationNameValid && (
                                            <p className="text-xs text-red-500 flex gap-2" role="alert">
                                                <HelpCircle size={14} /> {isOrganizationNameValid}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {step === 2 && (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Label htmlFor="subOrgName">Sub-organization Name</Label>
                                            <HelpHint text="Sub-organizations help you organize teams and projects within your main organization." />
                                        </div>
                                        <Input
                                            id="subOrgName"
                                            value={formData.subOrgName}
                                            onBlur={() => handleOnBlur('subOrganizationName')}
                                            onChange={(e) => {
                                                setIsSubOrganizationNameValid(null);
                                                updateFormData('subOrgName', e.target.value);
                                            }}
                                            placeholder={`${formData.orgName}-sub`}
                                        />
                                        {isSubOrganizationNameValid && (
                                            <p className="text-xs text-red-500 flex gap-2" role="alert">
                                                <HelpCircle size={14} /> {isSubOrganizationNameValid}
                                            </p>
                                        )}
                                        <p className="text-sm text-muted-foreground">Default: {formData.orgName}-sub</p>
                                    </div>
                                )}

                                {step === 3 && (
                                    <div className="space-y-4">
                                        <div className="flex gap-2">
                                            <Input
                                                value={newMemberEmail}
                                                onChange={(e) => setNewMemberEmail(e.target.value)}
                                                placeholder="email@example.com"
                                                className="flex-1"
                                            />
                                            <select
                                                className="h-10 px-3 rounded-md border border-input bg-background"
                                                value={newMemberRole}
                                                onChange={(e) => setNewMemberRole(e.target.value)}
                                            >
                                                <option value="admin">Admin</option>
                                                <option value="editor">Editor</option>
                                            </select>
                                            <Button disabled={fetchingUser} onClick={addMember} type="button">
                                                Add
                                            </Button>
                                        </div>
                                        {isEmailValid && (
                                            <p className="text-xs text-red-500 flex gap-2" role="alert">
                                                <HelpCircle size={14} /> {isEmailValid}
                                            </p>
                                        )}
                                        {emailSending && (
                                            <Alert className="bg-blue-50 border-blue-200">
                                                <Mail className="h-4 w-4 text-blue-600" />
                                                <AlertDescription className="text-blue-800">
                                                    An email invitation will be sent to <strong>{emailSending}</strong> to join your organization.
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                        {formData.members.length > 0 ? (
                                            <div className="border rounded-md overflow-hidden">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-muted">
                                                        <tr>
                                                            <th className="text-left p-2">Email</th>
                                                            <th className="text-left p-2">Role</th>
                                                            <th className="text-left p-2">Status</th>
                                                            <th className="p-2 w-10"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.members.map((member, index) => (
                                                            <tr key={index} className="border-t">
                                                                <td className="p-2">{member.email}</td>
                                                                <td className="p-2 capitalize">{member.role}</td>
                                                                <td className="p-2">
                                                                    {member.status === 'registered' ? (
                                                                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                                                            Added
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                                                            Invitation Pending
                                                                        </Badge>
                                                                    )}
                                                                </td>
                                                                <td className="p-2">
                                                                    <Button variant="ghost" size="icon" onClick={() => removeMember(member.email)}>
                                                                        <X className="h-4 w-4" />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-6 text-sm text-muted-foreground border rounded-md">
                                                No members added yet
                                            </div>
                                        )}
                                    </div>
                                )}

                                {step === 4 && (
                                    <div className="space-y-4">
                                        <RadioGroup
                                            value={formData.redisConfig.useCommon ? 'common' : 'custom'}
                                            onValueChange={(value) => {
                                                clearRedisValidation();
                                                updateRedisConfig('useCommon', value === 'common');
                                            }}
                                            className="space-y-3"
                                        >
                                            <div className="flex items-start space-x-2 border rounded-md p-3 bg-green-50">
                                                <RadioGroupItem value="common" id="redis-common" className="mt-1" />
                                                <div className="grid gap-1">
                                                    <Label htmlFor="redis-common" className="font-medium cursor-pointer flex items-center justify-between gap-2">
                                                        Use common Redis instance
                                                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                                            Recommended
                                                        </Badge>
                                                    </Label>
                                                    <p className="text-sm text-muted-foreground">
                                                        Shared Redis infrastructure suitable for most use cases.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-start space-x-2 border rounded-md p-3">
                                                <RadioGroupItem value="custom" id="redis-custom" className="mt-1" />
                                                <div className="grid gap-1.5 w-full">
                                                    <Label htmlFor="redis-custom" className="font-medium cursor-pointer">
                                                        Use custom Redis instance
                                                    </Label>
                                                    {!formData.redisConfig.useCommon && (
                                                        <div className="space-y-3 mt-2">
                                                            {isInvalidRedis && (
                                                                <Alert className="bg-red-50 border-red-200 text-red-600">
                                                                    <HelpCircle className="h-4 w-4" />
                                                                    <AlertDescription className="text-red-800">
                                                                        {typeof isInvalidRedis === 'string' ? isInvalidRedis : 'Please add valid redis connection keys'}
                                                                    </AlertDescription>
                                                                </Alert>
                                                            )}
                                                            {redisConnectionValidated && (
                                                                <Alert className="bg-green-50 border-green-200 text-green-700">
                                                                    <CheckCircle2 className="h-4 w-4" />
                                                                    <AlertDescription className="text-green-800">
                                                                        Redis connection successful.
                                                                    </AlertDescription>
                                                                </Alert>
                                                            )}
                                                            <div className="space-y-2">
                                                                <Label htmlFor="redisHost">Redis Host</Label>
                                                                <Input
                                                                    disabled={checkingRedisConnection}
                                                                    id="redisHost"
                                                                    value={formData.redisConfig.host}
                                                                    onChange={(e) => updateRedisConfig('host', e.target.value)}
                                                                    placeholder="redis.example.com"
                                                                />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label htmlFor="redisPort">Redis Port</Label>
                                                                <Input
                                                                    disabled={checkingRedisConnection}
                                                                    id="redisPort"
                                                                    value={formData.redisConfig.port}
                                                                    onChange={(e) => updateRedisConfig('port', e.target.value)}
                                                                    placeholder="6379"
                                                                />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label htmlFor="redisPassword">Redis Password</Label>
                                                                <Input
                                                                    disabled={checkingRedisConnection}
                                                                    id="redisPassword"
                                                                    type="password"
                                                                    value={formData.redisConfig.password}
                                                                    onChange={(e) => updateRedisConfig('password', e.target.value)}
                                                                    placeholder="Enter your Redis password"
                                                                />
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                className="w-full"
                                                                disabled={checkingRedisConnection}
                                                                onClick={handleTestRedisConnection}
                                                            >
                                                                {checkingRedisConnection ? 'Testing connection...' : 'Test Connection'}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </RadioGroup>
                                    </div>
                                )}
                            </div>
                        </ScrollArea>

                        <DialogFooter className="px-6 py-4 border-t sm:justify-between gap-2">
                            <Button variant="outline" onClick={handleBack}>
                                {step === 1 ? 'Cancel' : 'Back'}
                            </Button>
                            <div className="flex gap-2">
                                {step === 3 && (
                                    <Button
                                        variant="ghost"
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => handleNext(false)}
                                    >
                                        Configure Redis
                                    </Button>
                                )}
                                <Button
                                    disabled={isNextDisabled}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => handleNext(step >= 3)}
                                >
                                    {isAdvancing ? 'Checking...' : step < 3 ? (
                                        <>
                                            Next
                                            <ChevronRight className="ml-2 h-4 w-4" />
                                        </>
                                    ) : (
                                        'Create Organization'
                                    )}
                                </Button>
                            </div>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
